import {Component, Input} from '@angular/core';
import {UserService} from '../../../../../../@core/services/user.service';
import {FamilyService} from '../../../../../../@shared/services/family-service';
import {EventsService} from '../../../../../../@shared/services/events-service';
import {AQH_FAM_0, AQH_FAM_1, AQH_FAM_2} from '../../../../../../@shared/models/input.module';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

export interface tempUser {
  firstName: string;
  lastName: string;
  riding_club: string;
  date_of_birth: string;
}

export interface UserSearchResult {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  profile_image_url?: string;
}

export interface FamilyMember {
  user: {
    user_id: string;
    first_name: string;
    last_name: string;
    full_name?: string;
    email?: string;
    profile_image_url?: string;
  };
  role: string;
}

@Component({
  selector: 'app-add-membership',
  standalone: false,
  templateUrl: './add-membership.html',
  styleUrl: './add-membership.css'
})
export class AddMembership {
  @Input() familyId: number
  @Input() existingMembers: FamilyMember[] = [];
  @Input() currentUserId: string = '';
  addChildAccount: boolean = false
  tempUser: tempUser
  tempUserProfileImage: File | null = null;

  // User search state
  searchQuery: string = '';
  searchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearching: boolean = false;
  isRemoving: { [key: string]: boolean } = {};
  private searchSubject = new Subject<string>();

  constructor(private userService: UserService,
              private familyService: FamilyService,
              private eventsService: EventsService,
              public modal: NgbActiveModal,
              private toastService: ToastService) {
    this.tempUser = {
      firstName: '',
      lastName: '',
      riding_club: '',
      date_of_birth: ''
    }
  }

  get existingMemberIds(): string[] {
    return this.existingMembers.map(m => m.user.user_id);
  }

  ngOnInit() {
    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  onSearchChange(query: string) {
    this.searchQuery = query;
    this.selectedUser = null;
    if (query.length >= 2) {
      this.searchSubject.next(query);
    } else {
      this.searchResults = [];
    }
  }

  performSearch(query: string) {
    this.isSearching = true;
    this.eventsService.searchUsers(query).subscribe({
      next: (results: UserSearchResult[]) => {
        // Filter out existing family members
        this.searchResults = results.filter(u => !this.existingMemberIds.includes(u.user_id));
        this.isSearching = false;
      },
      error: () => {
        this.searchResults = [];
        this.isSearching = false;
      }
    });
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.searchQuery = `${user.first_name} ${user.last_name}`;
    this.searchResults = [];
  }

  clearSelection() {
    this.selectedUser = null;
    this.searchQuery = '';
    this.searchResults = [];
  }


  addSelectedUser() {
    if (!this.selectedUser) {
      this.toastService.error('Please select a user');
      return;
    }

    let params: AQH_FAM_1 = {
      family_id: this.familyId,
      email: this.selectedUser.email,
      role: 'guardian'
    }
    this.familyService.addFamilyMemberByEmail(params).subscribe({
      next: (res) => {
        if (res) {
          this.toastService.success('Member added successfully');
          this.modal.close(true);
        }
      },
      error: (error) => {
        console.error('Error adding member:', error);
        this.toastService.error(error?.error?.reason || 'Failed to add member');
      }
    });
  }

  removeMember(member: FamilyMember) {
    if (!confirm(`Are you sure you want to remove ${member.user.first_name} ${member.user.last_name} from the family?`)) {
      return;
    }

    this.isRemoving[member.user.user_id] = true;

    const params: AQH_FAM_0 = {
      family_id: this.familyId,
      user_id: member.user.user_id
    };

    this.familyService.removeFamilyMember(params).subscribe({
      next: () => {
        this.toastService.success('Member removed successfully');
        // Remove from local list
        this.existingMembers = this.existingMembers.filter(m => m.user.user_id !== member.user.user_id);
        this.isRemoving[member.user.user_id] = false;
      },
      error: (error) => {
        console.error('Error removing member:', error);
        this.toastService.error(error?.error?.reason || 'Failed to remove member');
        this.isRemoving[member.user.user_id] = false;
      }
    });
  }

  canRemoveMember(member: FamilyMember): boolean {
    // Can't remove owner, and can't remove yourself
    return member.role !== 'owner' && member.user.user_id !== this.currentUserId;
  }

  getRoleBadgeClass(role: string): string {
    switch (role.toLowerCase()) {
      case 'owner': return 'badge-primary';
      case 'guardian': return 'badge-info';
      case 'child': return 'badge-light';
      default: return 'badge-secondary';
    }
  }

  getRoleLabel(role: string): string {
    switch (role.toLowerCase()) {
      case 'owner': return 'Owner';
      case 'guardian': return 'Guardian';
      case 'child': return 'Child';
      default: return role;
    }
  }

  addUserWithoutAccount() {
    // Validate required fields
    if (!this.tempUser.firstName || !this.tempUser.lastName) {
      this.toastService.error('First name and last name are required');
      return;
    }

    const params: AQH_FAM_2 = {
      family_id: this.familyId,
      first_name: this.tempUser.firstName,
      last_name: this.tempUser.lastName,
      date_of_birth: this.tempUser.date_of_birth,
      riding_club: this.tempUser.riding_club,
      role: 'child'
    };

    this.familyService.addChildMember(params).subscribe({
      next: (res) => {
        if (res) {
          this.toastService.success('Child added successfully');
          this.modal.close(true);
        }
      },
      error: (error) => {
        console.error('Error adding child:', error);
        this.toastService.error('Failed to add child');
      }
    });
  }

}
